/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Notifications button
 *
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @version 1.25.1
 * @since 1.24.0
 */

import { forwardRef, useEffect, useState } from 'react';

import { BadgeCounter, IconButton } from '@nexign/scc-ui-kit';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { icons } from '@/main/assets';
import { ImgStyled } from '@/main/components/common/Block/Img';
import {
    makeSelectUserNotificationPollingInterval,
    makeSelectUserNotificationLastUserActionInterval,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';

import { useGetNotificationsListQuery } from '../store';
import { getUnreadNotificationsCount } from '../utils/commonNotificationsUtils';

interface IUserNotificationsButtonProps {
    handleMenuOpen: () => void;
}

export const UserNotificationsButton = forwardRef<HTMLButtonElement | null, IUserNotificationsButtonProps>(
    ({ handleMenuOpen }, ref) => {
        const [lastUserActionTime, setLastUserActionTime] = useState(moment());
        const pollingInterval = useSelector(makeSelectUserNotificationPollingInterval());
        const lastUserActionInterval = useSelector(makeSelectUserNotificationLastUserActionInterval());

        useEffect(()=>{
            window.addEventListener('click', debounce(() => setLastUserActionTime(moment()), DEBOUNCE_DEFAULT_DELAY), true);
            return () => {
                window.removeEventListener('click', debounce(() => setLastUserActionTime(moment()), DEBOUNCE_DEFAULT_DELAY), true);
            }
        },[]);


        const { data } = useGetNotificationsListQuery(undefined, {
            pollingInterval: moment().diff(lastUserActionTime, 'milliseconds') < lastUserActionInterval ? pollingInterval : 0,
        });

        const unreadNotificationsCount = getUnreadNotificationsCount(data?.elements);

        return (
            <IconButton ref={ref} onClick={handleMenuOpen}>
                <BadgeCounter badgeContent={unreadNotificationsCount}>
                    <ImgStyled className="medium" src={icons.bellIcon} />
                </BadgeCounter>
            </IconButton>
        );
    },
);
